import { useMemo } from 'react';
import type { PortfolioData } from '../../firebase/types/portfolio.types';
import { submitContactMessage } from '../../firebase/services/contact.service';
import AboutSection from './AboutSection';
import AchievementsSection from './AchievementsSection';
import ContactSection from './ContactSection';
import EducationSection from './EducationSection';
import ExperienceSection from './ExperienceSection';
import FaqSection from './FaqSection';
import FeaturedSection from './FeaturedSection';
import FreelanceSection from './FreelanceSection';
import Hero from './Hero';
import MediaSection from './MediaSection';
import ProcessSection from './ProcessSection';
import ProjectsSection from './ProjectsSection';
import ServicesSection from './ServicesSection';
import SiteFooter from './SiteFooter';
import SiteHeader from './SiteHeader';
import SkillsSection from './SkillsSection';
import TestimonialsSection from './TestimonialsSection';
import { buildNav, showAbout, showFaq, showServices, showSkills } from './model';

export default function PortfolioPage({
  data,
  portfolioUserId,
}: {
  data: PortfolioData;
  portfolioUserId: string | null;
}) {
  const nav = useMemo(() => buildNav(data, !!portfolioUserId), [data, portfolioUserId]);
  const indexFor = (id: string) => nav.find((item) => item.id === id)?.index ?? '';
  const onSubmitContact = portfolioUserId
    ? (payload: { name: string; email: string; message: string }) =>
        submitContactMessage({ ...payload, portfolioUserId })
    : undefined;

  return (
    <>
      <SiteHeader name={data.fullName} items={nav} />
      <main id="top" tabIndex={-1} className="outline-none">
        <Hero data={data} />
        {showAbout(data) ? <AboutSection data={data} index={indexFor('about')} /> : null}
        {showSkills(data) ? <SkillsSection data={data} index={indexFor('skills')} /> : null}
        {data.experience?.length ? (
          <ExperienceSection experience={data.experience} index={indexFor('experience')} />
        ) : null}
        {data.projects?.length ? <ProjectsSection projects={data.projects} index={indexFor('projects')} /> : null}
        {data.featuredProjects?.length ? (
          <FeaturedSection projects={data.featuredProjects} index={indexFor('featured')} />
        ) : null}
        {data.freelanceProjects?.length ? (
          <FreelanceSection projects={data.freelanceProjects} index={indexFor('freelance')} />
        ) : null}
        {showServices(data) ? <ServicesSection data={data} index={indexFor('services')} /> : null}
        {data.processSteps?.length ? <ProcessSection steps={data.processSteps} index={indexFor('process')} /> : null}
        {data.education?.length ? <EducationSection education={data.education} index={indexFor('education')} /> : null}
        {data.achievements?.length ? (
          <AchievementsSection achievements={data.achievements} index={indexFor('achievements')} />
        ) : null}
        {data.testimonials?.length ? (
          <TestimonialsSection testimonials={data.testimonials} index={indexFor('testimonials')} />
        ) : null}
        {data.mediaItems?.length ? <MediaSection items={data.mediaItems} index={indexFor('media')} /> : null}
        {showFaq(data) && data.faqItems ? <FaqSection items={data.faqItems} index={indexFor('faq')} /> : null}
        {nav.some((item) => item.id === 'contact') ? (
          <ContactSection data={data} index={indexFor('contact')} onSubmitContact={onSubmitContact} />
        ) : null}
      </main>
      <SiteFooter name={data.fullName} location={data.location} />
    </>
  );
}
